import {
  CatalogueImportModel,
  RowValidationCatalogueError,
  ValidationStatus,
  ColumnValidationResult,
  ImportResult,
  ImportStatus,
  SystemReferenceData,
} from 'src/pages/Catalog/Components/CatalogueImportDialogue/CustomHooks/types';
import { useCallback, useState } from 'react';
import { parseCsvAndValidateColumns } from 'src/utils/CommonHelpers/sharedCSVUtils';
import {
  buildPropertyNameSet,
  buildTypeNamesFromItemTypes,
  isNonEmptyRow,
  mapRowToCatalogueModel,
  validateCatalogueRows,
} from 'src/pages/Catalog/Components/CatalogueImportDialogue/CustomHooks/helpers';
import { toast } from 'react-toastify';
import { addBulkCatalogs, getAllItemTypes } from 'src/services/client';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { fetchStandardPropertyData } from 'src/utils/CommonDataFetchingUtils/useProperties';
import { IProperty } from 'src/pages/Reporting/ReportingFormTypes';

export interface UseCatalogueImportReturn {
  file: File | null;
  validationStatus: ValidationStatus;
  columnValidation: ColumnValidationResult;
  parsedRecords: CatalogueImportModel[];
  rowValidationErrors: RowValidationCatalogueError[];
  importStatus: ImportStatus;
  importResult: ImportResult | null;
  apiError: string | null;
  processFile: (file: File) => void;
  runImport: (userId: string) => Promise<void>;
  reset: () => void;
}

export const REQUIRED_CATALOGUE_COLUMNS: string[] = [
  'Name',
  'Type',
  'Warranty',
  'Make',
  'Model',
  'Properties',
];

export const useCatalogueImport = (): UseCatalogueImportReturn => {
  const user = useSelector(selectAuthenticatedUser);

  const [file, setFile] = useState<File | null>(null);
  const [validationStatus, setValidationStatus] =
    useState<ValidationStatus>('idle');
  const [columnValidation, setColumnValidation] =
    useState<ColumnValidationResult>({
      found: [],
      missing: [],
    });
  const [parsedRecords, setParsedRecords] = useState<CatalogueImportModel[]>(
    []
  );
  const [rowValidationErrors, setRowValidationErrors] = useState<
    RowValidationCatalogueError[]
  >([]);
  const [importStatus, setImportStatus] = useState<ImportStatus>('idle');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const processFile = (selectedFile: File) => {
    setFile(selectedFile);
    setValidationStatus('validating');
    setApiError(null);
    setRowValidationErrors([]);

    const reader = new FileReader();

    reader.onload = async (e: ProgressEvent<FileReader>) => {
      const text = e.target?.result as string;

      const {
        data,
        columnValidation: colValidation,
        isValid,
      } = parseCsvAndValidateColumns(text, REQUIRED_CATALOGUE_COLUMNS);

      setColumnValidation(colValidation);

      if (!isValid) {
        setValidationStatus('invalid');
        setParsedRecords([]);
        setRowValidationErrors([]);
        return;
      }

      const nonEmptyRows: any[] = data.filter(isNonEmptyRow);

      const mapped: CatalogueImportModel[] = nonEmptyRows.map(
        mapRowToCatalogueModel
      );

      try {
        const [itemTypesResponse, properties]: [any, IProperty[]] =
          await Promise.all([
            getAllItemTypes(),
            fetchStandardPropertyData(user.id, user.roles[0].role.name),
          ]);

        const itemTypes: any[] = await itemTypesResponse.json();

        const propertyNames: Set<string> = buildPropertyNameSet(properties);
        const typeNames: Set<string> = buildTypeNamesFromItemTypes(itemTypes);

        const referenceData: SystemReferenceData = { typeNames, propertyNames };

        const errors: RowValidationCatalogueError[] =
          validateCatalogueRows(mapped, referenceData);

        if (errors.length > 0) {
          setRowValidationErrors(errors);
          setParsedRecords(mapped);
          setValidationStatus('invalid');
          return;
        }

        setParsedRecords(mapped);
        setRowValidationErrors([]);
        setValidationStatus('valid');
      } catch (error: any) {
        console.error('Failed to fetch catalogue items:', error);
        setApiError(
          error.message || 'Failed to load reference data for validation.'
        );
        setValidationStatus('invalid');
      }
    };

    reader.onerror = () => {
      setValidationStatus('invalid');
      setColumnValidation({ found: [], missing: REQUIRED_CATALOGUE_COLUMNS });
    };

    reader.readAsText(selectedFile);
  };

  const runImport = async (userId: string): Promise<void> => {
    if (!parsedRecords.length) return;

    try {
      setImportStatus('importing');
      setApiError(null);

      const payload = {
        userId,
        items: parsedRecords,
      };

      const response: any = await addBulkCatalogs(payload);

      if (response.status === 200) {
        const responseJson = await response.json();

        if (responseJson?.statusCode === 200) {
          setImportResult({ totalRecords: parsedRecords.length });
          setImportStatus('success');

          toast.success(
            `${parsedRecords.length} catalog item(s) imported successfully!`,
            { autoClose: 8000, position: 'top-center' }
          );
        } else {
          throw new Error(responseJson?.message || 'Import failed');
        }
      } else {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(
          errorBody?.message || `Import failed (${response.status})`
        );
      }
    } catch (error: any) {
      console.error('Catalogue import error:', error);
      setApiError(error.message || 'Something went wrong — please try again.');
      setImportStatus('error');

      toast.error('Catalogue import failed. Please try again.', {
        autoClose: 12000,
        position: 'top-center',
      });
    }
  };

  const reset = useCallback((): void => {
    setFile(null);
    setValidationStatus('idle');
    setColumnValidation({ found: [], missing: [] });
    setParsedRecords([]);
    setImportStatus('idle');
    setImportResult(null);
    setApiError(null);
  }, []);

  return {
    file,
    validationStatus,
    columnValidation,
    parsedRecords,
    rowValidationErrors,
    importStatus,
    importResult,
    apiError,
    processFile,
    runImport,
    reset,
  };
};